import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'

function createCompareciente() {
  return reactive({
    isLoaded: false,

    // Basic properties
    id: null,
    creado: null,
    actualizado: null,
    activo: null,
    disabled: false,
    cliente_id: null,
    cliente: null,
    persona: null,
    nombre: null,
    paterno: null,
    materno: null,
    email: null,
    rfc: null,
    facebook: null,
    twitter: null,
    instagram: null,
    linkedin: null,

    // Persona Física
    fecha_nacimiento: null,
    lugar_nacim_ciudad: null,
    lugar_nacim_estado: null,
    lugar_nacim_pais: null,
    curp: null,
    ocupacion: null,
    giro_negocios: null,
    alias: null,
    sexo: null,
    pais_residencia: null,
    estado_civil: null,
    identificacion_conyuge: null,
    regimen_patrimonial: null,
    identificacion_tipo: null,
    identificacion_autoridad: null,
    identificacion_numero: null,
    identificacion_tipo_otro: null,
    identificacion_autoridad_otro: null,
    estatus_migratorio: null,
    fecha_ingreso_pais: null,

    // Persona Moral
    denominacion_social: null,
    fecha_constitucion: null,
    actividad: null,
    giro: null,
    objeto_social: null,
    domicilio_social: null,
    instrumento_numero: null,
    instrumento_numero_fecha: null,
    instrumento_fedatario: null,
    instrumento_fedatario_nombre: null,
    instrumento_fedatario_numero: null,
    instrumento_fedatario_estado: null,
    instrumento_rpf_folio: null,
    instrumento_rpf_folio_fecha: null,
    instrumento_rpf_folio_lugar: null,
    entidad_extranjera: null,
    datos_prueba_constitucion: null,

    // Fideicomiso
    fiduciario: null,
    nombre_fideicomiso: null,
    fideicomitente: null,
    tipo_fideicomiso: null,
    tipo_fideicomiso_otro: null,
    fecha_creacion_fideicomiso: null,
    folio_inscripcion: null,
    rfc_fideicomiso: null,
    email_fideicomiso: null,
    tel_fideicomiso: null,

    // Arrays
    nacionalidades: [],
    documentos: [],
    domicilios: [],
    telefonos: [],
    representantes: [],

    // Service flags
    completado: false,
    cat_servicio_id: null,
    servicio_id: null,
    tipo_poder: null,
    limitaciones_poder: null,
    vigencia_poder: null,
    actua_derecho_propio: null,
    es_beneficiario: null,

    // Current editing objects
    currentDomicilio: {
      tieneExtranjero: true,
      tieneTipo: true,
      compareciente: true,
      extranjero: null,
      mapUrl: null
    },
    currentTelefono: {},
    currentRepresentante: {
      domicilios: [],
      telefonos: [],
      instrumento_numero: null,
      instrumento_numero_fecha: null,
      instrumento_fedatario: null,
      instrumento_fedatario_nombre: null,
      instrumento_fedatario_numero: null,
      instrumento_fedatario_estado: null,
      instrumento_rpf_folio: null,
      instrumento_rpf_folio_fecha: null,
      instrumento_rpf_folio_lugar: null
    },
    currentDocumento: null,

    // Originals
    compareciente_original: {},
    domicilios_originales: [],
    telefonos_originales: [],
    representantes_originales: [],
    documentos_originales: [],

    // Current IDs
    currentDomicilioId: null,
    currentTelefonoId: null,
    currentRepresentanteId: null,
    currentRepresentanteDomicilioId: null,
    currentRepresentanteTelefonoId: null,
    currentDocumentoId: null,
    current_representantes_id: null,

    // Form options
    optionsFisicas: [],
    optionsFisicasRepresentadas: [],
    optionsMorales: [],
    optionsFideicomisos: [],
    cat_nacionalidades: []
  })
}

export const useComparecientesStore = defineStore(
  'comparecientes',
  () => {
    // State: array of comparecientes + active index
    const comparecientes = ref([])
    const currentIndex = ref(-1)

    // Computed: the currently selected compareciente
    const currentCompareciente = computed(() => {
      return comparecientes.value[currentIndex.value] || null
    })

    // Getters (based on currentCompareciente)
    const isPersonaFisica = computed(() =>
      currentCompareciente.value?.persona === 'Física'
    )
    const isPersonaMoral = computed(() =>
      currentCompareciente.value?.persona === 'Moral'
    )
    const isFideicomiso = computed(() =>
      currentCompareciente.value?.persona === 'Fideicomiso'
    )
    const fullName = computed(() => {
      const c = currentCompareciente.value
      if (!c) return ''
      return c.persona === 'Física'
        ? `${c.nombre || ''} ${c.paterno || ''} ${c.materno || ''}`.trim()
        : c.denominacion_social || ''
    })
    const hasRequiredDocuments = computed(() =>
      !!currentCompareciente.value?.documentos.length
    )
    const hasRequiredAddresses = computed(() =>
      !!currentCompareciente.value?.domicilios.length
    )
    const hasRequiredPhones = computed(() =>
      !!currentCompareciente.value?.telefonos.length
    )

    // Actions
    function addCompareciente(initialData = {}) {
      const newC = createCompareciente()
      Object.assign(newC, initialData)
      comparecientes.value.push(newC)
      currentIndex.value = comparecientes.value.length - 1
    }

    function selectCompareciente(index) {
      if (index >= 0 && index < comparecientes.value.length) {
        currentIndex.value = index
      }
    }

    function removeCompareciente(index) {
      if (index >= 0 && index < comparecientes.value.length) {
        comparecientes.value.splice(index, 1)
        // adjust currentIndex
        currentIndex.value =
          comparecientes.value.length === 0
            ? -1
            : Math.min(currentIndex.value, comparecientes.value.length - 1)
      }
    }

    function resetCurrentCompareciente() {
      if (currentCompareciente.value) {
        Object.assign(currentCompareciente.value, createCompareciente())
      }
    }

    function initializeCurrentCompareciente(data) {
      if (currentCompareciente.value) {
        Object.assign(currentCompareciente.value, data)
        // currentCompareciente.value.isLoaded = true
      }
    }

    return {
      // State
      comparecientes,
      currentIndex,
      currentCompareciente,

      // Getters
      isPersonaFisica,
      isPersonaMoral,
      isFideicomiso,
      fullName,
      hasRequiredDocuments,
      hasRequiredAddresses,
      hasRequiredPhones,

      // Actions
      addCompareciente,
      selectCompareciente,
      removeCompareciente,
      resetCurrentCompareciente,
      initializeCurrentCompareciente
    }
  },
  {
    persist: true
  }
)
